/**
 * Configuration Setting Reducer
 *
 * Manages all state for the Configuration Setting page:
 * - Programs CRUD
 * - Product Families
 * - Category duplicate check
 * - Program options (cascading)
 *
 * All types are imported from the single source of truth: src/types/configurationTypes.ts
 */

import ConfigurationApiTypes from "../types/configurationApiTypes";
import { ConfigurationState } from "../../types/configurationTypes";

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: ConfigurationState = {
  // Programs list
  programs: [],
  programsLoading: false,
  programsError: null,

  // Single program detail
  programDetail: null,
  programDetailLoading: false,
  programDetailError: null,

  // Create program
  createProgramLoading: false,
  createProgramResult: null,
  createProgramError: null,

  // Update programs
  updateProgramsLoading: false,
  updateProgramsResult: null,
  updateProgramsError: null,

  // Update programs status
  updateStatusLoading: false,
  updateStatusResult: null,
  updateStatusError: null,

  // Product families
  productFamilies: [],
  productFamiliesLoading: false,
  productFamiliesError: null,

  // Create product family
  createProductFamilyLoading: false,
  createProductFamilyResult: null,
  createProductFamilyError: null,

  // Category duplicate check
  categoryCheckLoading: false,
  categoryCheckResult: null,
  categoryCheckError: null,

  // Program options
  programOptions: null,
  programOptionsLoading: false,
  programOptionsError: null,
};

// ─── Reducer ──────────────────────────────────────────────────────────────────

const configurationReducer = (state = initialState, action: any): ConfigurationState => {
  switch (action.type) {
    // ── Get Programs (list) ─────────────────────────────────────────────────
    case ConfigurationApiTypes.GET_PROGRAMS_REQUEST:
      return { ...state, programsLoading: true, programsError: null };
    case ConfigurationApiTypes.GET_PROGRAMS_SUCCESS:
      return {
        ...state,
        programsLoading: false,
        programs: action.payload?.data || [],
      };
    case ConfigurationApiTypes.GET_PROGRAMS_FAILURE:
      return {
        ...state,
        programsLoading: false,
        programsError: action.payload?.message || "Failed to retrieve programs",
      };

    // ── Get Program Detail ──────────────────────────────────────────────────
    case ConfigurationApiTypes.GET_PROGRAM_DETAIL_REQUEST:
      return { ...state, programDetailLoading: true, programDetailError: null };
    case ConfigurationApiTypes.GET_PROGRAM_DETAIL_SUCCESS:
      return {
        ...state,
        programDetailLoading: false,
        programDetail: action.payload?.data || null,
      };
    case ConfigurationApiTypes.GET_PROGRAM_DETAIL_FAILURE:
      return {
        ...state,
        programDetailLoading: false,
        programDetailError: action.payload?.message || "Failed to retrieve program detail",
      };

    // ── Create Program ──────────────────────────────────────────────────────
    case ConfigurationApiTypes.CREATE_PROGRAM_REQUEST:
      return { ...state, createProgramLoading: true, createProgramError: null, createProgramResult: null };
    case ConfigurationApiTypes.CREATE_PROGRAM_SUCCESS:
      return {
        ...state,
        createProgramLoading: false,
        createProgramResult: {
          program_id: action.payload?.data?.program_id || "",
          message: action.payload?.message || "Program created successfully",
        },
      };
    case ConfigurationApiTypes.CREATE_PROGRAM_FAILURE:
      return {
        ...state,
        createProgramLoading: false,
        createProgramError: action.payload?.message || "Failed to create program",
      };

    // ── Update Programs (single or batch) ───────────────────────────────────
    case ConfigurationApiTypes.UPDATE_PROGRAMS_REQUEST:
      return { ...state, updateProgramsLoading: true, updateProgramsError: null, updateProgramsResult: null };
    case ConfigurationApiTypes.UPDATE_PROGRAMS_SUCCESS:
      return {
        ...state,
        updateProgramsLoading: false,
        updateProgramsResult: action.payload?.data || null,
      };
    case ConfigurationApiTypes.UPDATE_PROGRAMS_FAILURE:
      return {
        ...state,
        updateProgramsLoading: false,
        updateProgramsError: action.payload?.message || "Failed to update programs",
      };

    // ── Update Programs Status (enable/disable) ─────────────────────────────
    case ConfigurationApiTypes.UPDATE_PROGRAMS_STATUS_REQUEST:
      return { ...state, updateStatusLoading: true, updateStatusError: null, updateStatusResult: null };
    case ConfigurationApiTypes.UPDATE_PROGRAMS_STATUS_SUCCESS:
      return {
        ...state,
        updateStatusLoading: false,
        updateStatusResult: {
          message: action.payload?.message || "Status updated",
          updated_count: action.payload?.data?.updated_count || 0,
        },
      };
    case ConfigurationApiTypes.UPDATE_PROGRAMS_STATUS_FAILURE:
      return {
        ...state,
        updateStatusLoading: false,
        updateStatusError: action.payload?.message || "Failed to update program status",
      };

    // ── Get Product Families ────────────────────────────────────────────────
    case ConfigurationApiTypes.GET_PRODUCT_FAMILIES_REQUEST:
      return { ...state, productFamiliesLoading: true, productFamiliesError: null };
    case ConfigurationApiTypes.GET_PRODUCT_FAMILIES_SUCCESS:
      return {
        ...state,
        productFamiliesLoading: false,
        productFamilies: action.payload?.data || [],
      };
    case ConfigurationApiTypes.GET_PRODUCT_FAMILIES_FAILURE:
      return {
        ...state,
        productFamiliesLoading: false,
        productFamiliesError: action.payload?.message || "Failed to retrieve product families",
      };

    // ── Create Product Family ───────────────────────────────────────────────
    case ConfigurationApiTypes.CREATE_PRODUCT_FAMILY_REQUEST:
      return { ...state, createProductFamilyLoading: true, createProductFamilyError: null, createProductFamilyResult: null };
    case ConfigurationApiTypes.CREATE_PRODUCT_FAMILY_SUCCESS:
      return {
        ...state,
        createProductFamilyLoading: false,
        createProductFamilyResult: action.payload?.data || null,
      };
    case ConfigurationApiTypes.CREATE_PRODUCT_FAMILY_FAILURE:
      return {
        ...state,
        createProductFamilyLoading: false,
        createProductFamilyError: action.payload?.message || "Failed to create product family",
      };

    // ── Check Category Duplicate ────────────────────────────────────────────
    case ConfigurationApiTypes.CHECK_CATEGORY_DUPLICATE_REQUEST:
      return { ...state, categoryCheckLoading: true, categoryCheckError: null, categoryCheckResult: null };
    case ConfigurationApiTypes.CHECK_CATEGORY_DUPLICATE_SUCCESS:
      return {
        ...state,
        categoryCheckLoading: false,
        categoryCheckResult: {
          exists: action.payload?.exists || false,
          message: action.payload?.message || "",
        },
      };
    case ConfigurationApiTypes.CHECK_CATEGORY_DUPLICATE_FAILURE:
      return {
        ...state,
        categoryCheckLoading: false,
        categoryCheckError: action.payload?.message || "Failed to check category",
      };

    // ── Get Program Options (cascading) ─────────────────────────────────────
    case ConfigurationApiTypes.GET_PROGRAM_OPTIONS_REQUEST:
      return { ...state, programOptionsLoading: true, programOptionsError: null };
    case ConfigurationApiTypes.GET_PROGRAM_OPTIONS_SUCCESS:
      return {
        ...state,
        programOptionsLoading: false,
        programOptions: action.payload?.data || null,
      };
    case ConfigurationApiTypes.GET_PROGRAM_OPTIONS_FAILURE:
      return {
        ...state,
        programOptionsLoading: false,
        programOptionsError: action.payload?.message || "Failed to retrieve program options",
      };

    // ── Clear transient results/errors ──────────────────────────────────────
    // Resets all result/error fields to prevent stale notifications on re-mount
    case ConfigurationApiTypes.CLEAR_CONFIGURATION_RESULTS:
      return {
        ...state,
        createProgramResult: null,
        createProgramError: null,
        updateProgramsResult: null,
        updateProgramsError: null,
        updateStatusResult: null,
        updateStatusError: null,
        programsError: null,
        productFamiliesError: null,
        programOptionsError: null,
        createProductFamilyResult: null,
        createProductFamilyError: null,
        categoryCheckResult: null,
        categoryCheckError: null,
      };

    default:
      return state;
  }
};

export default configurationReducer;
